import React from "react";
import {
  FormControl,
  FormHelperText,
  Typography,
  Autocomplete,
  TextField,
  Box,
} from "@mui/material";

/**
 * Reusable MultiSelect component based on MUI Autocomplete.
 * Designed to match the project's design system with labels above the input.
 *
 * @param {string} name - Component name
 * @param {string|ReactNode} label - Label text or element
 * @param {Array} value - Selected values
 * @param {function} onChange - Change handler (event, newValue) => void
 * @param {Array} options - List of options
 * @param {function} getOptionLabel - Mapper from option to string
 * @param {function} isOptionEqualToValue - Comparison function for options
 * @param {string|ReactNode} helperText - Optional hint text below the input
 * @param {string} error - Error message
 * @param {string} placeholder - Placeholder text
 * @param {boolean} multiple - Allow multiple selection (default: true)
 */
const MultiSelect = ({
  name,
  label,
  value = [],
  onChange,
  options = [],
  getOptionLabel,
  isOptionEqualToValue,
  helperText,
  error = "",
  placeholder = "Select items",
  disabled = false,
  required = false,
  multiple = true,
  id,
  sx = {},
  ...rest
}) => {
  const inputId = id || (name ? `multi-select-${name}` : undefined);

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      disabled={disabled}
      required={required}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          variant="body2"
          fontWeight={600}
          sx={{
            display: "block",
            mb: 0.5,
            color: error ? "error.main" : "text.secondary",
            transition: "color 0.2s ease-in-out",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}
      <Autocomplete
        id={inputId}
        multiple={multiple}
        options={options}
        value={value}
        onChange={onChange}
        getOptionLabel={
          getOptionLabel ||
          ((opt) => (typeof opt === "string" ? opt : opt?.label || ""))
        }
        isOptionEqualToValue={isOptionEqualToValue || ((a, b) => a === b)}
        disabled={disabled}
        /** Ensure the popper appears above Dialogs */
        slotProps={{
          popper: {
            sx: { zIndex: 10050 },
            placement: "bottom-start",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value?.length === 0 ? placeholder : ""}
            error={Boolean(error)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                minHeight: "42px",
                background: "#fff",
                paddingLeft: "12px !important",
                paddingTop: "8px !important",
                paddingBottom: "8px !important",
                paddingRight: "40px !important", // Space for dropdown arrow
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "6px",
                "& fieldset": {
                  borderWidth: "2px !important",
                  borderColor: error ? "error.main" : "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: error ? "error.main" : "#bce4cb", // colors.primary[200]
                },
                "&.Mui-focused fieldset": {
                  borderColor: error ? "error.main" : "#38915c", // colors.primary[500]
                },
                "&.Mui-error fieldset": {
                  borderColor: "error.main !important",
                },
                "& .MuiAutocomplete-endAdornment": {
                  top: "calc(50% - 14px)", // Center the icons vertically
                },
              },
              "& .MuiAutocomplete-input": {
                padding: "0 !important",
                minHeight: "24px",
                flexGrow: 1,
                width: "0 !important", // Allow it to shrink and wrap
                minWidth: "60px",
              },
              "& .MuiChip-root": {
                height: "28px",
                borderRadius: "8px",
                backgroundColor: "#f0fdf4", // Light green background
                border: "1px solid #bce4cb",
                "& .MuiChip-label": {
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: "#166534",
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: "16px",
                  color: "#38915c",
                  "&:hover": {
                    color: "#166534",
                  },
                },
              },
            }}
          />
        )}
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            borderRadius: "12px",
          },
          ...sx,
        }}
        {...rest}
      />
      {(error || helperText) && (
        <FormHelperText
          sx={{
            ml: 0,
            color: error ? "error.main" : "text.secondary",
          }}
        >
          {error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default MultiSelect;
